/**
 * Atmospheric Scattering Component
 *
 * Renders a physically-based atmosphere glow around a spherical planet
 * using Rayleigh + Mie scattering with ray marching.
 *
 * Based on:
 * - GPU Gems 2 Ch.16 (Sean O'Neil) - Accurate Atmospheric Scattering
 * - github.com/wwwtyro/glsl-atmosphere - GLSL reference implementation
 *
 * Handles camera both inside and outside the atmosphere shell via
 * dynamic face-culling and mathematical ray-sphere intersection.
 */

import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const vertexShader = `
  varying vec3 vWorldPosition;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPos.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  #define PI 3.141592653589793
  #define PRIMARY_STEPS 16
  #define LIGHT_STEPS 8

  uniform vec3  uSunDirection;
  uniform float uSunIntensity;
  uniform float uPlanetRadius;
  uniform float uAtmosphereRadius;
  uniform vec3  uRayleighCoeff;
  uniform float uMieCoeff;
  uniform float uRayleighScaleHeight;
  uniform float uMieScaleHeight;
  uniform float uMieG;

  varying vec3 vWorldPosition;

  // Ray-sphere intersection → (tNear, tFar). Returns tNear > tFar on miss.
  vec2 raySphere(vec3 origin, vec3 dir, float radius) {
    float a = dot(dir, dir);
    float b = 2.0 * dot(dir, origin);
    float c = dot(origin, origin) - radius * radius;
    float d = b * b - 4.0 * a * c;
    if (d < 0.0) return vec2(1e5, -1e5);
    float sd = sqrt(d);
    return vec2((-b - sd) / (2.0 * a), (-b + sd) / (2.0 * a));
  }

  void main() {
    vec3 rayOrigin = cameraPosition;
    vec3 rayDir    = normalize(vWorldPosition - cameraPosition);
    vec3 sunDir    = normalize(uSunDirection);

    // Atmosphere intersection
    vec2 atmoHit = raySphere(rayOrigin, rayDir, uAtmosphereRadius);
    if (atmoHit.x > atmoHit.y) discard;

    // Planet (ocean) intersection
    vec2 planetHit = raySphere(rayOrigin, rayDir, uPlanetRadius);

    // March segment: from atmosphere entry (or camera if inside) to exit or planet surface
    float tStart = max(atmoHit.x, 0.0);
    float tEnd   = atmoHit.y;
    if (planetHit.x < planetHit.y && planetHit.x > 0.0) {
      tEnd = min(tEnd, planetHit.x);
    }

    float stepSize = (tEnd - tStart) / float(PRIMARY_STEPS);

    // Phase functions (computed once)
    float mu   = dot(rayDir, sunDir);
    float mumu = mu * mu;
    float gg   = uMieG * uMieG;
    float phaseR = 3.0 / (16.0 * PI) * (1.0 + mumu);
    float phaseM = 3.0 / (8.0 * PI)
                 * ((1.0 - gg) * (mumu + 1.0))
                 / (pow(1.0 + gg - 2.0 * mu * uMieG, 1.5) * (2.0 + gg));

    // Accumulate scattering along primary ray
    vec3  totalR = vec3(0.0);
    vec3  totalM = vec3(0.0);
    float odR    = 0.0;   // optical depth Rayleigh
    float odM    = 0.0;   // optical depth Mie

    for (int i = 0; i < PRIMARY_STEPS; i++) {
      vec3  samplePos = rayOrigin + rayDir * (tStart + (float(i) + 0.5) * stepSize);
      float height    = length(samplePos) - uPlanetRadius;

      // Density at sample
      float dR = exp(-height / uRayleighScaleHeight) * stepSize;
      float dM = exp(-height / uMieScaleHeight)      * stepSize;
      odR += dR;
      odM += dM;

      // Secondary ray toward sun
      float lightStep = raySphere(samplePos, sunDir, uAtmosphereRadius).y / float(LIGHT_STEPS);
      float lodR = 0.0;
      float lodM = 0.0;

      for (int j = 0; j < LIGHT_STEPS; j++) {
        vec3  lPos = samplePos + sunDir * ((float(j) + 0.5) * lightStep);
        float lH   = length(lPos) - uPlanetRadius;
        lodR += exp(-lH / uRayleighScaleHeight) * lightStep;
        lodM += exp(-lH / uMieScaleHeight)      * lightStep;
      }

      // Combined attenuation (camera path + sun path)
      vec3 attn = exp(-(uMieCoeff * (odM + lodM) + uRayleighCoeff * (odR + lodR)));
      totalR += dR * attn;
      totalM += dM * attn;
    }

    vec3 color = uSunIntensity * (phaseR * uRayleighCoeff * totalR
                                + phaseM * uMieCoeff      * totalM);

    // Tone-map to prevent harsh clipping
    color = 1.0 - exp(-color);

    // Alpha from luminance so black regions are fully transparent
    float alpha = clamp(dot(color, vec3(0.299, 0.587, 0.114)) * 1.8, 0.0, 1.0);

    gl_FragColor = vec4(color, alpha);
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface AtmosphereProps {
  /** Planet surface radius (must match ocean radius) */
  radius: number;
  /** Multiplier for atmosphere outer boundary (default 1.25 = 25% above surface) */
  atmosphereScale?: number;
  /** Sun direction vector (will be normalized) */
  sunPosition?: THREE.Vector3;
  /** Sun light intensity (default 22) */
  sunIntensity?: number;
}

export function Atmosphere({
  radius,
  atmosphereScale = 1.40,
  sunPosition = new THREE.Vector3(0.5, 0.3, 0.8),
  sunIntensity = 22.0,
}: AtmosphereProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const atmosphereRadius = radius * atmosphereScale;

  // Scattering coefficients tuned relative to planet radius.
  // Rayleigh scale height ~12% of radius, Mie ~4% — large enough to fill the thick shell.
  const rayleighScaleHeight = radius * 0.12;
  const mieScaleHeight      = radius * 0.04;

  // Scattering coefficients: blue-weighted Rayleigh, uniform Mie.
  // These are 1/distance units, inversely proportional to radius.
  const rayleighCoeff = new THREE.Vector3(3.8, 9.0, 15.5).multiplyScalar(1 / radius);
  const mieCoeff      = 5.0 / radius;

  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uSunDirection:        { value: sunPosition.clone().normalize() },
        uSunIntensity:        { value: sunIntensity },
        uPlanetRadius:        { value: radius },
        uAtmosphereRadius:    { value: atmosphereRadius },
        uRayleighCoeff:       { value: rayleighCoeff },
        uMieCoeff:            { value: mieCoeff },
        uRayleighScaleHeight: { value: rayleighScaleHeight },
        uMieScaleHeight:      { value: mieScaleHeight },
        uMieG:                { value: 0.758 },
      },
      side: THREE.FrontSide,
      transparent: true,
      depthWrite: false,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, atmosphereScale, sunIntensity]);

  useEffect(() => {
    return () => { material.dispose(); };
  }, [material]);

  // Dynamically swap face-culling so the atmosphere is visible whether
  // the camera is outside or inside the shell.
  useFrame((state) => {
    if (!material) return;
    material.uniforms.uSunDirection.value.copy(sunPosition).normalize();
    const dist = state.camera.position.length();
    material.side = dist > atmosphereRadius ? THREE.FrontSide : THREE.BackSide;
  });

  return (
    <mesh ref={meshRef} renderOrder={1}>
      <icosahedronGeometry args={[atmosphereRadius, 5]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}
